import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// toast available for future notifications
import { BACKEND_URL } from '../config/api';
import { getApiHeaders } from '../utils/auth';
import { useAccessControl } from '../hooks/useAccessControl';
import AccountSwitcher from './AccountSwitcher';
import { Button } from './ui/button';

const Navigation = ({ user, onLogout, isCollapsed = false, onToggleCollapse }) => {
  const { canAccess, role: _role, loading: permissionsLoading } = useAccessControl();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [_hasCohosts, setHasCohosts] = useState(false);

  // Fetch cohosts to determine if Finance Hub should be shown
  // Skip for users without cohost access (e.g. viewer role) to avoid 403 errors
  const canAccessCohosts = canAccess('cohosts.section');
  useEffect(() => {
    if (!canAccessCohosts) return;

    const fetchCohosts = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        
        const response = await fetch(`${BACKEND_URL}/api/cohosts`, {
          headers: getApiHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          const cohosts = data.data || data || [];
          setHasCohosts(Array.isArray(cohosts) && cohosts.length > 0);
        }
      } catch (error) {
        console.error('Error fetching cohosts for navigation:', error);
      }
    };
    
    fetchCohosts();
    
    // Listen for cohost changes (when user creates/deletes a cohost)
    const handleCohostChange = () => fetchCohosts();
    window.addEventListener('cohostChanged', handleCohostChange);
    return () => window.removeEventListener('cohostChanged', handleCohostChange);
  }, [user, canAccessCohosts]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = viewportWidth >= 1024;
  const showLabels = !isDesktop || !isCollapsed;

  useEffect(() => {
    if (isDesktop) {
      setIsMobileMenuOpen(false);
    }
  }, [isDesktop]);

  const handleToggle = () => {
    if (isDesktop) {
      if (onToggleCollapse) {
        onToggleCollapse();
      }
    } else {
      setIsMobileMenuOpen((prev) => !prev);
    }
  };


  // Removed: Business profile toast notification
  // Business info will be collected contextually when sharing reports or subscribing

  // Base navigation items with access control
  // Each item specifies the feature key for access checking
  const allNavigationItems = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: 'fas fa-tachometer-alt',
      testid: 'nav-dashboard',
      feature: 'dashboard.view', // All roles can view
    },
    {
      path: '/roi-calculator',
      label: 'ROI Calculator',
      icon: 'fas fa-chart-line',
      testid: 'nav-roi-calculator',
      feature: 'analytics.view', // All roles can view analytics - positioned after Dashboard
    },
    {
      path: '/vehicle-management',
      label: 'Vehicles',
      icon: 'fas fa-car',
      testid: 'nav-vehicles',
      feature: 'vehicles.view', // All roles can view
    },
    {
      path: '/co-hosts',
      label: 'Co-Hosts',
      icon: 'fas fa-users',
      testid: 'nav-cohosts',
      feature: 'cohosts.section', // Only show to users who can manage cohosts (owner, admin, manager)
    },
    {
      path: '/finance-hub',
      label: 'Finance Hub',
      icon: 'fas fa-wallet',
      testid: 'nav-finance-hub',
      feature: 'finance.view', // Universal - all users can view their finances
    },
    // REMOVED: Payout Verification is now integrated into Finance Hub Command Center
    // Users access it via /finance-hub when there are items needing attention
    // Pricing accessible via Settings > Billing > Change Plan, or /pricing URL directly
    // Hidden for now — migration service section temporarily removed from sidebar
    // {
    //   path: '/migration-service',
    //   label: 'Migration Service',
    //   icon: 'fas fa-rocket',
    //   testid: 'nav-migration-service',
    //   feature: 'settings.profile', // All authenticated users can access
    // },
    {
      path: '/settings',
      label: 'Settings',
      icon: 'fas fa-cog',
      testid: 'nav-settings',
      feature: 'settings.profile', // All roles can access profile at minimum
    },
    {
      // The /help route existed but had no way in from the app, so users hitting
      // a problem had nowhere obvious to go. No `feature` gate: everyone,
      // on every tier, must always be able to reach support.
      path: '/help',
      label: 'Help & Support',
      icon: 'fas fa-circle-question',
      testid: 'nav-help',
    }
  ];
  
  // Filter navigation items based on access control
  // Finance Hub is now universal - available to ALL users for tracking expenses/earnings
  // Wait for permissions to load before filtering to prevent flash of missing items
  const navigationItems = useMemo(() => {
    // While loading, show all items to prevent flash
    if (permissionsLoading) {
      return allNavigationItems;
    }
    return allNavigationItems.filter(item => {
      if (!item.feature) return true;
      return canAccess(item.feature);
    });
  }, [canAccess, permissionsLoading]);

  return (
    <>
      {/* Mobile Header - Fixed Top Bar */}
      {!isDesktop && (
        <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-40 flex items-center px-4 justify-between lg:hidden">
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggle}
              className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
            </button>
            <div className="flex items-center gap-2">
              <img src="/assets/images/logo_icon.png" alt="Turnover Manager" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Turnover
              </span>
            </div>
          </div>
        </header>
      )}


      {/* Mobile Overlay */}
      <AnimatePresence>
        {!isDesktop && isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleToggle}
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* Navigation Sidebar */}
      <motion.nav
        initial={false}
        animate={{ x: isDesktop ? 0 : isMobileMenuOpen ? 0 : -280 }}
        transition={{ type: "tween", duration: 0.25, ease: "easeInOut" }}
        className={`fixed left-0 top-0 h-full ${isCollapsed ? 'lg:w-20' : 'lg:w-64'} w-64 bg-white shadow-xl border-r border-gray-200 z-50`}
      >
        <div className="flex flex-col h-full">
          {/* Logo and brand */}
          <div className={`border-b border-gray-200 ${showLabels ? 'p-4' : 'py-3 px-2'}`}>
            <div className={`flex items-center ${showLabels ? 'justify-between' : 'flex-col gap-2'}`}>
              <a 
                href="https://www.turnover-manager.com?loggedIn=true" 
                className={`flex items-center ${showLabels ? 'gap-3' : 'justify-center'} cursor-pointer hover:opacity-80 transition-opacity`}
              >
                <img src="/assets/images/logo_icon.png" alt="Turnover Manager" className={`${showLabels ? 'w-10 h-10' : 'w-9 h-9'} rounded-xl`} />
                {showLabels && (
                  <div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      Turnover
                    </h1>
                    <p className="text-xs text-gray-500">Fleet Management</p>
                  </div>
                )}
              </a>
              {/* Desktop collapse/expand toggle - inside sidebar */}
              {isDesktop && (
                <button
                  onClick={handleToggle}
                  className={`p-1.5 hover:bg-gray-100 rounded-lg transition-colors ${!showLabels ? 'w-full flex justify-center' : ''}`}
                  aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  <i className={`fas ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'} text-gray-400 text-sm`}></i>
                </button>
              )}
            </div>
          </div>

          {/* Account Switcher - shows business name with building icon */}
          <div className={`border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 ${showLabels ? 'px-3 py-2' : 'py-2 flex justify-center'}`}>
            <AccountSwitcher showExpanded={showLabels} />
          </div>

          {/* Navigation items */}
          <div className={`flex-1 overflow-y-auto ${showLabels ? 'p-4' : 'p-2'}`}>
            <div className={`space-y-1 ${!showLabels ? 'flex flex-col items-center' : ''}`}>
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={item.testid}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`relative flex items-center rounded-lg transition-all duration-200 ${
                      showLabels ? 'gap-3 px-4 py-3' : 'justify-center w-12 h-12'
                    } ${
                      isActive
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {isActive && showLabels && (
                      <span className="absolute left-0 top-2 bottom-2 w-1 bg-white rounded-r-full"></span>
                    )}
                    <i className={`${item.icon} ${showLabels ? 'text-lg' : 'text-xl'} ${isActive ? 'text-white' : 'text-gray-500'}`}></i>
                    {showLabels && <span className="font-medium">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Logout button */}
          <div className={`border-t border-gray-200 ${showLabels ? 'p-4' : 'p-2 flex justify-center'}`}>
            <Button
              onClick={onLogout}
              variant="outline"
              className={`${showLabels ? 'w-full h-11 justify-start gap-3' : 'w-12 h-12 p-0 justify-center'}`}
              data-testid="logout-button"
            >
              <i className={`fas fa-sign-out-alt text-gray-500 ${!showLabels ? 'text-xl' : ''}`}></i>
              {showLabels && <span>Sign Out</span>}
            </Button>
          </div>
        </div>
    </motion.nav>
    </>
  );
};

export default Navigation;
